import Seo from "../components/apps/Seo";
import { Link, useParams } from "react-router-dom";
import { getApp } from "../data/apps";
import {
  AppIcon,
  AppStoreBadge,
  Screenshot,
  Stars,
  body,
  display,
} from "../components/apps/AppBits";

export default function AppDetailPage() {
  const { slug } = useParams();
  const app = getApp(slug);

  if (!app) {
    return (
      <main style={body} className="bg-[#FFF9F2] min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p style={display} className="text-3xl font-semibold text-[#241633]">
          Hmm, that app isn't here
        </p>
        <Link to="/apps" className="text-[#B33FA0] font-semibold underline">
          Back to all apps
        </Link>
      </main>
    );
  }

    <Seo
      title={`${app.name} — ${app.subtitle} | Zinnobyte`}
      description={`${app.tagline} ${app.features.slice(0, 2).join(". ")}.`}
      path={`/apps/${app.slug}`}
      image={app.icon}
      app={app}
    />
  const comingSoon = app.status === "coming-soon";

  return (
    <main style={body} className="bg-[#FFF9F2] min-h-screen pb-20">
      {/* ------------------------------------------------------- header */}
      <section
        className="text-white"
        style={{
          background: `linear-gradient(120deg, #241663 0%, ${app.gradient[1]} 70%, ${app.gradient[0]} 100%)`,
        }}
      >
        <div className="max-w-5xl mx-auto px-6 pt-10 pb-12 md:pt-14 md:pb-16">
          <Link
            to="/apps"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-8"
          >
            <span aria-hidden>←</span> All apps
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <AppIcon app={app} size={112} />
            <div className="flex-1">
              <h1 style={display} className="text-3xl md:text-4xl font-semibold">
                {app.name}
              </h1>
              <p className="text-white/85 text-lg">{app.subtitle}</p>
              <p className="text-white/70 text-sm mt-1">{app.price}</p>
            </div>
            <div className="sm:self-center">
              {comingSoon ? (
                <span
                  style={display}
                  className="inline-block rounded-full bg-white/15 border border-white/40 px-5 py-2.5 font-semibold"
                >
                  Coming soon
                </span>
              ) : (
                app.appStoreUrl && <AppStoreBadge href={app.appStoreUrl} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- stat strip */}
      <section className="max-w-5xl mx-auto px-6">
        <div className="-mt-6 rounded-2xl bg-white border border-[#F0E7DA] px-6 py-4 flex flex-wrap justify-around gap-x-8 gap-y-3 shadow-[0_16px_36px_-24px_rgba(36,22,51,0.4)]">
          <Stat label={app.rating ? `${app.ratingCount}` : "Ratings"}>
            {app.rating ? (
              <span className="flex items-center gap-1.5">
                <span className="font-bold text-[#241633]">{app.rating}</span>
                <Stars value={app.rating} size={13} />
              </span>
            ) : (
              "—"
            )}
          </Stat>
          <Stat label="Age">{app.ageRating}</Stat>
          <Stat label="Category">{app.category}</Stat>
          {app.size && <Stat label="Size">{app.size}</Stat>}
          {app.requires && <Stat label="Requires">{app.requires}</Stat>}
        </div>
      </section>

      {/* -------------------------------------------------- screenshots */}
      <section className="max-w-5xl mx-auto px-6 mt-12">
        <h2 style={display} className="text-2xl font-semibold text-[#241633] mb-5">
          Preview
        </h2>
        <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory">
          {app.screenshots.map((src, i) => (
            <Screenshot
              key={src}
              src={src}
              alt={`${app.name} screenshot ${i + 1}`}
              accent={app.gradient[1]}
            />
          ))}
        </div>
      </section>

      {/* ------------------------------------------- about + features */}
      <section className="max-w-5xl mx-auto px-6 mt-12 grid gap-10 md:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 style={display} className="text-2xl font-semibold text-[#241633] mb-4">
            About {app.name}
          </h2>
          {app.description.map((para) => (
            <p key={para.slice(0, 24)} className="text-[#4A4238] leading-relaxed mb-4">
              {para}
            </p>
          ))}
        </div>
        <div>
          <h2 style={display} className="text-2xl font-semibold text-[#241633] mb-4">
            Highlights
          </h2>
          <ul className="space-y-2.5">
            {app.features.map((f) => (
              <li key={f} className="flex gap-2.5 text-[#4A4238]">
                <span aria-hidden className="mt-0.5" style={{ color: app.gradient[1] }}>
                  ✦
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ------------------------------------------------------ reviews */}
      {app.reviews.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 mt-14">
          <h2 style={display} className="text-2xl font-semibold text-[#241633] mb-5">
            What people are saying
          </h2>
          <div className="grid gap-5 md:grid-cols-3">
            {app.reviews.map((r) => (
              <figure
                key={r.title}
                className="rounded-2xl bg-white border border-[#F0E7DA] p-5"
              >
                <Stars value={r.rating} size={14} />
                <figcaption style={display} className="mt-2 font-semibold text-[#241633]">
                  {r.title}
                </figcaption>
                <blockquote className="mt-2 text-sm text-[#4A4238] leading-relaxed">
                  {r.body}
                </blockquote>
                <p className="mt-3 text-xs text-[#8A7F70]">
                  {r.author} · {r.date}
                </p>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* --------------------------------------------------- footer CTA */}
      <section className="max-w-5xl mx-auto px-6 mt-16 text-center">
        {comingSoon ? (
          <div
            className="rounded-3xl px-8 py-12 text-white"
            style={{
              background: `linear-gradient(120deg, ${app.gradient[0]}, ${app.gradient[1]})`,
            }}
          >
            <h2 style={display} className="text-3xl font-semibold">
              {app.name} is almost ready
            </h2>
            <p className="mt-2 text-white/85">
              We're putting the finishing touches on it. Check back soon.
            </p>
          </div>
        ) : (
          app.appStoreUrl && (
            <div className="rounded-3xl bg-white border border-[#F0E7DA] px-8 py-12">
              <h2 style={display} className="text-3xl font-semibold text-[#241633]">
                Ready to guffaw?
              </h2>
              <p className="mt-2 mb-6 text-[#8A7F70]">
                Free to download — start making faces in seconds.
              </p>
              <AppStoreBadge href={app.appStoreUrl} />
              {app.privacyUrl && (
                <p className="mt-6 text-xs">
                  <a href={app.privacyUrl} className="text-[#B33FA0] underline">
                    Privacy policy
                  </a>
                </p>
              )}
            </div>
          )
        )}
      </section>
    </main>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-center">
      <p className="text-[11px] uppercase tracking-wide text-[#8A7F70]">{label}</p>
      <p className="text-sm font-semibold text-[#241633] mt-0.5">{children}</p>
    </div>
  );
}
